import React from "react";
import router from "umi/router";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { notification } from "antd";
import { extend } from "umi-request";
import { revokeToken, refreshToken } from "@/utils/session";
import ErrorRequestMessage from "@/components/ErrorRequestMessage/ErrorRequestMessage";
import { MediaPurpose } from "./enum";
import { getLocalStorage } from "./storage";

const codeMessage = {
  200: "The server successfully returned the requested data. ",
  201: "Successfully created/modified one or more new resources. ",
  202: "Your request is being processed. ",
  204: "Successfully returned requested data, but no content sent. ",
  400: "Bad request. ",
  401: "Unauthorized request. ",
  403: "Forbidden request. ",
  404: "Not Found. ",
  406: "Requested format is not available. ",
  410: "Requested resource is no longer available. ",
  422: "Uprocessable entity. ",
  500: "Internal server error. ",
  502: "Bad gateway error. ",
  503: "Service unavailable. ",
  504: "Gateway timeout. ",
};

const doNotShowANotificationEndpoints = {
  "/connect/token": [],
  "/api/core-service/transporters/info-from-dot": [],
  "/api/media-service/media/upload": [
    { mediaPurpose: MediaPurpose.BillOfLading.toString() },
    { mediaPurpose: MediaPurpose.BillOfLadingAdditional.toString() },
  ],
};

function splitURLandParams(urlString) {
  if (!urlString) {
    return ["", new URLSearchParams()];
  }

  const url = new URL(urlString);
  return [url.pathname, url.searchParams];
}

const errorHandler = async error => {
  const { response } = error;

  if (response && response.status) {
    var errorText = response.message || codeMessage[response.status];
    const { url } = response;
    if (url.includes("Loads")) {
      return response;
    }
    if (url.includes("connect/token")) {
      notification.error({
        message: `Error`,
        description: "invalid login credentials",
      });
    } else {
      await response
        .json()
        .then(r => {
          let displayExistingError = true;
          if (r.status === 404) return;
          if (r.error && r.error.message) errorText = r.error.message;
          else if (r?.errors) {
            for (let errorMessage of Object.values(r?.errors)) {
              notification.error({
                message: "Error",
                description: errorMessage,
              });
              displayExistingError = false;
            }
          }

          if (displayExistingError) {
            notification.error({
              message: `Error`,
              description: errorText,
            });
          }
        })
        .catch(e => console.log(e));
    }
  } else if (!response) {
    const { request } = error;
    const { url: rawUrl } = request;
    const [pathname, params] = splitURLandParams(rawUrl);

    const description = error.message || "No response. You might not be connected to the internet";

    const matchingParams = doNotShowANotificationEndpoints[pathname];
    let shouldNotify = matchingParams === undefined;

    if (Array.isArray(matchingParams) && matchingParams.length > 0) {
      // Endpoint tagged as notifiable
      // Check if all params are in the list

      shouldNotify = matchingParams.every(combination => {
        const entries = Object.entries(combination);

        // If the specific params combination is found, do not notify
        return entries.every(([key, value]) => params.get(key) !== value);
      });
    }

    if (shouldNotify) {
      notification.error({
        message: error.message ? "Error" : "Network Issue",
        description: <ErrorRequestMessage errorMessage={description} />,
      });

      return {
        error: error.message ?? "Error",
      };
    } else {
      return {
        error: description,
      };
    }
  }
  if (!Array.isArray(response)) {
    if (response.status === 404) {
      return [];
    }
  } else {
    return response;
  }

  if (errorText.startsWith("Cannot post multiple instances of vin")) {
    return errorText;
  }
};

const request = extend({
  errorHandler,
  getResponse: true,
});

request.interceptors.request.use((url, options) => {
  let dataAuth = getLocalStorage("dataAuth");
  let token = dataAuth?.access_token;
  if (token) {
    return {
      url,
      options: {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        },
      },
    };
  }
  return { url, options };
});

let isRefreshing = false;

const loggableURLs = [REACT_APP_API_BASE_URL_NEW, REACT_APP_IDENTITY_SERVER_BASE_URL];

request.interceptors.response.use(async (response, options) => {
  const clone = response.clone();
  let result;

  try {
    result = await clone.json();
  } catch (error) {
    //
  }

  let microservicesBaseURL;

  for (let i = 0; i < loggableURLs.length; i++) {
    if (response.url.includes(loggableURLs[i])) {
      microservicesBaseURL = loggableURLs[i];
    }
  }

  if (microservicesBaseURL) {
    const endpoint = response.url.slice(microservicesBaseURL.length);
    const isErrorHTTPStatus = response.status >= 400;

    const logMessage = `Network Log - STATUS: ${response.status}, METHOD: ${options.method}, ENDPOINT: ${endpoint}`;
    const logData = {
      response: {
        headers: JSON.stringify(response.headers),
        status: response.status,
        url: response.url,
        body: result,
      },
    };

    if (isErrorHTTPStatus) {
      datadogLogs.logger.error(logMessage, logData);
      if (response.status >= 500) {
        datadogRum.addError(new Error(`Internal Server Error - ${response.status} - ${endpoint}`));
      }
    } else {
      datadogLogs.logger.log(logMessage, logData);
    }
  }

  const { error, error_description } = result || {};

  if (response && response?.ok) {
    isRefreshing = false;
    return response;
  }

  if (response.status === 401 && !isRefreshing) {
    isRefreshing = true;
    const refreshResponse = await refreshToken();
    if (refreshResponse?.ok) window.location.reload();
    return;
  }

  if (
    (response.status === 400 && response.url === `${REACT_APP_IDENTITY_SERVER_BASE_URL}/connect/token`) ||
    (response.status === 401 && isRefreshing)
  ) {
    const params = new URLSearchParams(options.body);
    const tryingToImpersonate = params.get("UserId") !== null;
    if (tryingToImpersonate) {
      notification.error({
        message: "Error",
        description: "Something went wrong when trying to impersonate this user",
      });
      return;
    }

    isRefreshing = false;
    let dataAuth = getLocalStorage("dataAuth");
    let token = dataAuth?.refresh_token;

    if (token) {
      await revokeToken({ token });
    }

    router.push("/user/login");
  }

  throw new Error(error_description || error?.details || error?.message);
});

export default request;
